import React, { useEffect, useMemo, useState } from 'react';
import { ModelDefinitionDto, ModelTypes } from '../../models/APIModels';
import { cloneDeep } from 'lodash';
import { Controller, FormProvider, SubmitHandler, useForm } from 'react-hook-form';
import { ExternalLinkType, ModelEditorStep } from '../../enums';
import { Box, Step, StepButton, Stepper } from '@mui/material';
import ModelInfo from './ModelInfo';
import AIEngineConfiguration from './AIEngineConfiguration';
import { ApiHelper } from '../../common/ApiHelper';
import { uploadFile } from '../../common/Upload';
import useAppUtils from '../../Utils/useAppUtils';
import Loader from '../Loader';
import { ModelForm } from './ModelForm';
import Button from '../Button';
import clientUtils from '../../Utils/clientUtils';
import modelUtils from '../../Utils/modelUtils';
import Progress from '../Loaders/Progress';
 

interface IProps {
  model: ModelDefinitionDto;
}

interface Step {
  id: ModelEditorStep;
  title: string;
}

const infoStep = { id: ModelEditorStep.Info, title: 'Info' };
const aiEngineConfigStep = { id: ModelEditorStep.AiEngineConfigStep, title: 'AI Engine Configuration' };

const ModelEditor = ({ model }: IProps) => {
  //
  const [activeStep, setActiveStep] = useState(0);
  // const [skipped, setSkipped] = useState(new Set<number>());
  const [availableSteps, setAvailableSteps] = useState<Array<Step>>([]);
  const [currentStep, setCurrentStep] = useState<Step>(infoStep);
  const [completed, setCompleted] = React.useState({});
  const [isSaving, setIsSaving] = useState(false);
  const { alert, confirm } = useAppUtils();
  const [isHostedModel, setIsHostedModel] = useState<boolean>(model && model.modelType === 'HostedModel');

  const modelFormValues = useForm<ModelDefinitionDto>({
    defaultValues: useMemo(() => {
      // console.log('model form changed', model);
      return model;
    }, [model]),
  });

  const reset = () => {
    setActiveStep(0);
    setCompleted({});
  };
  const totalSteps = () => {
    return availableSteps.length;
  };

  const completedSteps = () => {
    return Object.keys(completed).length;
  };

  const isLastStep = () => {
    return activeStep === totalSteps() - 1;
  };

  const allStepsCompleted = () => {
    return completedSteps() === totalSteps();
  };
  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
    const nextStep = availableSteps[activeStep - 1];
    setCurrentStep(nextStep);
  };

  const onSubmit = async (data: ModelDefinitionDto) => {
    // console.log('onSubmit', { data });
  };

  const handleStep = (step) => () => {
    setActiveStep(step);
    const nextStep = availableSteps[step];
    setCurrentStep(nextStep);
  };

  // Get content based on which step is active
  const getStepContent = (step: Step) => {
    switch (step.id) {
      case ModelEditorStep.Info:
        return <ModelInfo formValues={modelFormValues} orginalModel={model} />;
      case ModelEditorStep.AiEngineConfigStep:
        return <AIEngineConfiguration formValues={modelFormValues} />;
      default:
        return 'Unknown step';
    }
  };

  const handleNext = () => {
    const newActiveStepNum =
      isLastStep() && !allStepsCompleted()
        ? // It's the last step, but not all
        // steps have been completed,
        // find the first step that has been completed
        availableSteps.findIndex((step, i) => !(i in completed))
        : activeStep + 1;

    const nextStep = availableSteps[newActiveStepNum];
    setActiveStep(newActiveStepNum);
    setCurrentStep(nextStep);
  };

  const onSave: SubmitHandler<ModelDefinitionDto> = async (form: ModelDefinitionDto) => {

    let modelForm: ModelDefinitionDto = cloneDeep(form);
    if (modelForm.modelType === ModelTypes.HostedModel) {
      if (modelForm.aiEngineConfig.inputs.length === 0) {
        alert('The model must include at least one Input', 'error');
        return;
      }
      if (modelForm.aiEngineConfig.outcomes.length === 0) {
        alert('The model must include at least one Outcome.', 'error');
        return;
      }
      

      // ## Validations for Model Alias if alias alrady reserved for another models .....
      const { success, data }:any = await modelUtils.modelAliasFetcher(modelForm.modelAlias);
      if (success) {
          if (data.attachedItemId !== modelForm.modelId && !(modelForm.modelAlias === '')) {
              alert(`Model Alias is already reserved for another model !`, 'error');
              return;
          }
      }


      // ## show alert if user puts same model alias .....
      // success && (data.attachedItemId == modelForm.modelId && alert(`Model Alias is already reserved for this model !`, 'info'))


      // ## putting validation to only allow small case letters and _ and - .....
      if (!(modelUtils.validateAliasInputString(modelForm.modelAlias)) && !(modelForm.modelAlias === '')) {
          alert(`Model Alias can only contain lowercase letters, "-", and "_"`, 'error');
          return
      }  
      const invalidInput = modelForm.aiEngineConfig.inputs.find((input) => clientUtils.isEmpty(input.name));
      const invalidOutcome = modelForm.aiEngineConfig.outcomes.find((outcome) => clientUtils.isEmpty(outcome.name));
      if (invalidInput) {
        alert(`Input type for variable Name is missing!.`, 'error');
        return;
      }
      if (invalidOutcome) {
        alert(`Outcome type for variable Name is missing!.`, 'error');
        return;
      }
    }
    modelForm.supportingLinks = modelForm.supportingLinks.filter((l) => l.linkType !== ExternalLinkType.None);
    setIsSaving(true);
    try {
      if (form.modelLongDescriptionValue) {
        const uploadLinkInfoResponse = await ApiHelper.GetUploadLinkInfo(form.modelId);
        if (uploadLinkInfoResponse && uploadLinkInfoResponse.data) {
          const url = uploadLinkInfoResponse.data.publicUploadLink;
          const uploadResponse = await uploadFile(form.modelLongDescriptionValue, url);
          modelForm.modelData.filePaths = {
            ...modelForm.modelData.filePaths,
            MODELLONGDESCRIPTION: uploadLinkInfoResponse.data.destinationPath,
          };
          modelForm.modelLongDescriptionValue = '';
        }
      }
      const aiEngineConfig = modelForm.aiEngineConfig;
      if (aiEngineConfig && aiEngineConfig.inputs) {
        for (let i = 0; i < aiEngineConfig.inputs.length; i++) {
          const input = aiEngineConfig.inputs[i];
          if (input && input.options) {
            input.options = input.options.map((o) => {
              return {
                ...o,
                optionFriendlyName: o.optionFriendlyName ? o.optionFriendlyName : o.optionValue,
              };
            });
          }
        }
      }
       
      const updateModelResponse = await ApiHelper.UpsertModel(modelForm);
      if (updateModelResponse && updateModelResponse.data) {
        alert('Model Updated');
      }
      setIsSaving(false);
    } catch (e) {
      alert('Unable to save model', 'error');
      console.error('Unable to save model', e);
      setIsSaving(false);
    }
  };

  useEffect(() => {
    let steps: Array<Step> = [infoStep];
    if (isHostedModel) steps.push(aiEngineConfigStep);
    setAvailableSteps(steps);
  }, []);

  const watchAllFields = modelFormValues.watch((v, { name, type }) => {
    let steps: Array<Step> = [infoStep];;
    if (v && name === 'modelType' && v.modelType === 'HostedModel') {
      setIsHostedModel(v.modelType === 'HostedModel');
      const aiEngineConfig = modelUtils.buildAiEngineConfig(v.aiEngineConfig, v.modelType);
      modelFormValues.setValue('aiEngineConfig', aiEngineConfig);
      steps.push(aiEngineConfigStep);
      setAvailableSteps(steps);
    }
  });






  // ➡️ Debug Code's .....
  // useEffect(() => {
  //   console.clear()
  //   console.log(modelFormValues)
  // }, [modelFormValues])


  return (
    <div className="step-container">
      <div className="step-header-container">
        {isSaving && <Progress  progressLabel='Please Wait ...'/>}
        <Stepper nonLinear activeStep={activeStep}>
          {availableSteps.map((label, index) => (
            <Step key={label.id} completed={completed[index]}>
              <StepButton color="inherit" onClick={handleStep(index)}>
                {label.title}
              </StepButton>
            </Step>
          ))}
        </Stepper>
      </div>

      <div className="step-body-container">
        <React.Fragment>
          <FormProvider {...modelFormValues}>
            <form className="fullHeight" onSubmit={modelFormValues.handleSubmit(onSave)}>
              <Box className="step-content-container">
                {/* // Populate the content pane based on the active step */}
                {getStepContent(currentStep)}
              </Box>
              <Box className="step-button-container">
                <Box sx={{ flex: '1 1 auto', textAlign: 'right' }}>
                  {activeStep !== 0 && (
                    <Button
                      variant="contained"
                      rounded
                      style={{ marginRight: '3px' }}
                      disabled={activeStep === 0}
                      onClick={handleBack}
                    >
                      Back
                    </Button>
                  )}
                  <Button variant="contained" rounded onClick={() => window.open(`/models/${model.modelId}/view?dev=true`,"_blank")}>
                    Try this Model
                  </Button>
                  {!isLastStep() && (
                    <Button
                      variant="contained"
                      rounded
                      style={{ marginRight: '3px' }}
                      onClick={handleNext}
                      disabled={isLastStep()}
                    >
                      Next
                    </Button>
                  )}
                  <Button variant="contained" rounded onClick={modelFormValues.handleSubmit(onSave)}>
                    Save
                  </Button>
                </Box>
              </Box>
            </form>
          </FormProvider>
        </React.Fragment>
      </div>
    </div>
  );
};

export default ModelEditor;
